import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { SingleSelectDropdown } from './common/SingleSelectDropdown';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import styles from './LogoManagementModal.module.css';
import '../styles/buttons.css';

interface LogoManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandId: string;
}

const BACKGROUND_OPTIONS = [
  { value: '', label: '指定なし' },
  { value: 'white_back', label: '白バック' },
  { value: 'black_back', label: '黒バック' },
  { value: 'transparent', label: '透過' },
];

const COLOR_TYPE_OPTIONS = [
  { value: '', label: '指定なし' },
  { value: 'full_color', label: 'フルカラー' },
  { value: 'monochrome', label: 'モノクロ（黒）' },
  { value: 'white', label: '白抜き' },
  { value: 'solid_color', label: '単色（ブランドカラー）' },
  { value: 'grayscale', label: 'グレースケール' },
  { value: 'gradient', label: 'グラデーション' },
  { value: 'flat', label: 'フラット' },
];

const LAYOUT_OPTIONS = [
  { value: '', label: '指定なし' },
  { value: 'horizontal', label: '横組み' },
  { value: 'vertical', label: '縦組み' },
  { value: 'square', label: 'スクエア' },
  { value: 'logotype', label: 'ロゴタイプのみ' },
  { value: 'symbol', label: 'シンボル' },
  { value: 'lockup', label: 'アイコン+ロゴ' },
  { value: 'app_icon', label: 'アプリアイコン' },
  { value: 'circle', label: 'サークル' },
];

const SUPABASE_URL = 'https://lirsarznxaidebqhgxch.supabase.co';

export default function LogoManagementModal({
  isOpen,
  onClose,
  brandId,
}: LogoManagementModalProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedLogoId, setSelectedLogoId] = useState<string>('');
  const [selectedLogoUrl, setSelectedLogoUrl] = useState<string | null>(null);
  const [_selectedLogoName, setSelectedLogoName] = useState<string>('');
  const [background, setBackground] = useState<string>('');
  const [colorType, setColorType] = useState<string>('');
  const [layout, setLayout] = useState<string>('');
  const [isDefault, setIsDefault] = useState(false);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const continueAfterAddRef = useRef(false);

  // Add logo mutation
  const addLogoMutation = useMutation({
    mutationFn: async (data: {
      logo_id: string;
      background?: string;
      color_type?: string;
      layout?: string;
      is_default: boolean;
      description?: string;
    }) => {
      const response = await apiClient.post(`/brands/${brandId}/logos`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand', brandId] });
      if (continueAfterAddRef.current) {
        continueAfterAddRef.current = false;
        resetForm();
      } else {
        onClose();
      }
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error || err?.message || '追加に失敗しました');
    },
  });

  // Upload logo mutation
  const uploadLogoMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post('/logos/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['logos'] });
      if (data.logo) {
        setSelectedLogoId(data.logo.id);
        setSelectedLogoName(data.logo.file_name || '');
        // パブリックURLを構築（bucket名を含める）
        if (data.logo.storage_path) {
          const bucket = data.logo.storage_bucket || 'brand-assets';
          setSelectedLogoUrl(`${SUPABASE_URL}/storage/v1/object/public/${bucket}/${data.logo.storage_path}`);
        }
      }
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error || err?.message || 'アップロードに失敗しました');
    },
  });

  const resetForm = () => {
    setSelectedLogoId('');
    setSelectedLogoUrl(null);
    setSelectedLogoName('');
    setBackground('');
    setColorType('');
    setLayout('');
    setIsDefault(false);
    setDescription('');
    setError(null);
    setIsDragOver(false);
  };

  // ファイルを処理してすぐにアップロード
  const processFile = (file: File) => {
    // 画像ファイルかチェック
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('PNG, JPG, SVG, WebP形式のファイルのみアップロードできます');
      return;
    }
    setError(null);
    // 即座にアップロード開始
    uploadLogoMutation.mutate(file);
  };

  // ファイル選択時
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    // inputをリセットして同じファイルを再選択できるようにする
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ドラッグ&ドロップハンドラー
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleSubmit = (e?: React.FormEvent, continueAdding = false) => {
    e?.preventDefault();
    setError(null);

    if (!selectedLogoId) {
      setError('ロゴをアップロードしてください');
      return;
    }

    continueAfterAddRef.current = continueAdding;
    addLogoMutation.mutate({
      logo_id: selectedLogoId,
      background: background || undefined,
      color_type: colorType || undefined,
      layout: layout || undefined,
      is_default: isDefault,
      description: description || undefined,
    });
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const isLoading = addLogoMutation.isPending || uploadLogoMutation.isPending;

  if (!isOpen) return null;

  return createPortal(
    // 入力内容の消失を防ぐため、オーバーレイのクリックでは閉じない (✕ ボタンのみ)
    <div className="modal-overlay modal-overlay--nested">
      <div className={`modal-card modal-card--xl ${styles.modal}`}>
        {/* ヘッダー */}
        <div className="modal-header">
          <h2 className="modal-title">ロゴを追加</h2>
          <button
            type="button"
            className="modal-close-button"
            onClick={onClose}
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>

        {/* 2カラムレイアウト */}
        <div className={styles.mainContent}>
          {/* 左カラム: ロゴプレビュー・アップロード */}
          <div
            className={`${styles.previewColumn} ${isDragOver ? styles.dragOver : ''}`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {uploadLogoMutation.isPending ? (
              <div className={styles.uploadingState}>
                <div className={styles.spinner} />
                <span>アップロード中...</span>
              </div>
            ) : selectedLogoUrl ? (
              <div className={styles.previewFull}>
                <img src={selectedLogoUrl} alt="ロゴプレビュー" />
              </div>
            ) : (
              <div
                className={`${styles.previewPlaceholder} ${isDragOver ? styles.dragOverPlaceholder : ''}`}
                onClick={() => fileInputRef.current?.click()}
              >
                {/* 区切り線の幅を文言 / ボタンの最大幅に合わせる (.or-divider-group) */}
                <div className="or-divider-group">
                  <span>{isDragOver ? 'ここにドロップ' : 'ロゴをドラッグ&ドロップ'}</span>
                  <div className="or-divider"><span>または</span></div>
                  <button
                    type="button"
                    className="btn-modal btn-modal-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    disabled={isLoading}
                  >
                    ファイルを選択
                  </button>
                </div>
                <p className={styles.helpText}>
                  PNG, JPG, SVG, WebP形式
                </p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={handleFileChange}
              className={styles.fileInput}
            />
          </div>

          {/* 右カラム: フォーム */}
          <div className={styles.formColumn}>
            <form onSubmit={handleSubmit} className={styles.form}>
              {error && <div className={styles.error}>{error}</div>}

              {/* 背景タイプ */}
              <div className={styles.formGroup}>
                <label className={styles.label}>背景</label>
                <SingleSelectDropdown
                  label="背景"
                  labelOnly
                  alwaysShowSelected
                  value={background}
                  onChange={setBackground}
                  options={BACKGROUND_OPTIONS}
                  disabled={isLoading}
                />
              </div>

              {/* 色タイプ */}
              <div className={styles.formGroup}>
                <label className={styles.label}>カラー</label>
                <SingleSelectDropdown
                  label="カラー"
                  labelOnly
                  alwaysShowSelected
                  value={colorType}
                  onChange={setColorType}
                  options={COLOR_TYPE_OPTIONS}
                  disabled={isLoading}
                />
              </div>

              {/* レイアウト */}
              <div className={styles.formGroup}>
                <label className={styles.label}>レイアウト</label>
                <SingleSelectDropdown
                  label="レイアウト"
                  labelOnly
                  alwaysShowSelected
                  value={layout}
                  onChange={setLayout}
                  options={LAYOUT_OPTIONS}
                  disabled={isLoading}
                />
              </div>

              {/* 説明 */}
              <div className={styles.formGroup}>
                <label className={styles.label}>説明</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={styles.textarea}
                  rows={2}
                  placeholder="このロゴの用途や説明..."
                  disabled={isLoading}
                />
              </div>

              {/* デフォルト設定 */}
              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                    disabled={isLoading}
                  />
                  <span>デフォルトとして設定</span>
                </label>
              </div>
            </form>
          </div>
        </div>

        {/* フッター */}
        <div className={styles.footer}>
          <div className={styles.footerLeft} />
          <div className={styles.footerRight}>
            <button
              type="button"
              className="btn-modal btn-modal-secondary"
              onClick={() => handleSubmit(undefined, true)}
              disabled={isLoading || !selectedLogoId}
            >
              {addLogoMutation.isPending && continueAfterAddRef.current ? '追加中...' : '続けて別のロゴを追加'}
            </button>
            <button
              type="button"
              className="btn-modal btn-modal-accent"
              onClick={() => handleSubmit()}
              disabled={isLoading || !selectedLogoId}
            >
              {addLogoMutation.isPending && !continueAfterAddRef.current ? '保存中...' : '保存して閉じる'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
